import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { message, Popover, Tooltip } from "antd";
import IconArrowDown from "./arrowDown.svg?react";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import SearchProductInput from "../SearchProductInput";
import SellingPoints from "../SellingPoints";
import VideoReference from "../VideoReference";
import { t } from "i18next";
import { CommonButton } from "@components/CommonButton";
import "./index.scss";
import { IconAiGradient, IconCredit, IconHelp } from "@icons/index";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import VideoLibraryModal from "../VideoLibraryModal";
import { useVideoRewrite } from "@pages/aiStudio/ScriptRewrite/hooks";
import { useQueryParams } from "@hooks/useQueryParams";
import { updateParams } from "@pages/aiStudio/ScriptRewrite/scriptRewriteSlice";
import ProductLibraryModal from "../ProductLibraryModal";
import { updateProductItem } from "@pages/aiStudio/ScriptRewrite/scriptRewriteSlice";
import MemberPopoverContent from "@components/MemberPopoverContent";
import { useMembershipLevel } from "@hooks/membership";
import { fetchAiCredits } from "@store/globalSlice";
import aiStudioService from "@apis/aiStudioService";
import report from "@report";
import { TestId } from "@shared/testIds";
import { useSerializeCallback } from "@kalodata/tools";
import { SelectLanguage } from "@components/SelectLanguage/SelectLanguage";

// 支持单选按钮组的 SelectGroup 组件
type SelectGroupOption = { label: string; value: string };
type SelectGroupProps = {
  options: SelectGroupOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  buttonClassName?: string;
  disabled?: boolean;
};

const SelectGroup = ({ options, value, onChange, className, buttonClassName, disabled }: SelectGroupProps) => {
  return (
    <div className={classNames("flex gap-[8px] flex-wrap", className)}>
      {options.map((opt) => (
        <div
          key={opt.value}
          className={classNames(
            "min-w-[82px] px-[16px] py-[8px] rounded border transition-colors text-center select-gradient-border",
            value === opt.value && "active",
            disabled ? "cursor-not-allowed!" : "select-gradient-border-effects",
            disabled && value !== opt.value && "disabled",
            buttonClassName,
          )}
          onClick={() => {
            if (disabled) return;
            onChange(opt.value);
          }}
        >
          {opt.label}
        </div>
      ))}
    </div>
  );
};

interface VideoParamsPanel {
  className?: string;
  showCard?: boolean;
}

const VideoParamsPanel = ({ className, showCard }: VideoParamsPanel) => {
  const [tryExampleLoading, setTryExampleLoading] = useState(false);
  const [originalVideoID]: any = useQueryParams(["id", "videoSource"]);
  const {
    country: _country,
    videoId,
    videoDetail,
    productItem,
    rewriteScriptList,
    status,
    hasProduct,
    target_age,
    target_gender,
    imitation_level,
    language,
    sellingPoints: sellingPointsData,
  } = useAppSelector((state) => state.scriptRewriteReducer);
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const [showAdvanced, setShowAdvanced] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const isDark = useAtomValue(themeActionAtom) === "dark";
  const [popoverWidth, setPopoverWidth] = useState<number>(0);
  const panelRef = useRef<HTMLDivElement>(null);

  const { loopAITask, reset, requestSellingPointers } = useVideoRewrite();

  const dispatch = useAppDispatch();
  const { isBasicUp } = useMembershipLevel();

  // 监听按钮宽度变化
  useEffect(() => {
    if (!panelRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPopoverWidth(entry.contentRect.width - 36);
      }
    });
    observer.observe(panelRef.current);
    setPopoverWidth(panelRef.current.offsetWidth - 36);
    return () => observer.disconnect();
  }, []);

  const renderGenerateBtn = () => {
    const btnDisabled =
      rewriteScriptList?.length === 4 ||
      totalRemain < 2 ||
      (!videoId && !productItem.data) ||
      (hasProduct && !productItem?.data);
    // 动态按钮配置
    const btnConfig = {
      disabled: btnDisabled,
      locked: !isBasicUp || totalRemain < 2,
      loading: status === "loading" || handleGenerateMore.isPending(),
      onClick: btnDisabled ? undefined : handleGenerateMore,
    };

    // 动态按钮文案
    const getButtonText = () => {
      if (status === "failed") return t("Retry");
      if (rewriteScriptList?.length === 2 && totalRemain >= 2) return t("Generate 2 more");
      return t("Generate");
    };

    // 动态 Popover content
    const getPopoverContent = () => {
      if (!isBasicUp) return <MemberPopoverContent desc="Upgrade to use the feature" action="Upgrade" />;
      else if (totalRemain < 2) return <MemberPopoverContent remainCredit={totalRemain} action="Credits" />;
    };

    const button = (
      <CommonButton
        data-testid={TestId.ScriptRewriteGenerate}
        size="large"
        type="primary"
        className="flex-1 h-[34px] px-[12px] border border-transparent w-full new-btn"
        {...btnConfig}
      >
        {getButtonText()}
        <div className="flex items-center gap-[2px] ml-[8px] pl-[4px] pr-[8px] h-[22px] leading-[22px] credit-tag rounded-full">
          <IconCredit width={16} height={16} />
          <span className="font-semibold text-[14px]">2</span>
        </div>
      </CommonButton>
    );

    const popoverContent = getPopoverContent();

    // 达到上限用 Tooltip
    if (rewriteScriptList?.length === 4 && status !== "loading") {
      return (
        <Tooltip title={t("The generation limit has been reached. Please refresh the page to continue.")}>
          {button}
        </Tooltip>
      );
    }

    if (popoverContent) {
      return (
        <Popover content={popoverContent} trigger="hover">
          {button}
        </Popover>
      );
    }

    return button;
  };

  const levelOptions = [
    { label: t("script_similar_level"), value: "standard" },
    { label: t("High Similarity"), value: "high_similarity" },
  ];

  const genderOptions = [
    { label: t("All"), value: "all" },
    { label: t("Male"), value: "male" },
    { label: t("Female"), value: "female" },
  ];
  const ageOptions = [
    { label: t("All"), value: "all" },
    { label: "18 ~ 24", value: "18-24" },
    { label: "25 ~ 34", value: "25-34" },
    { label: "35 ~ 44", value: "35-44" },
    { label: "45 ~ 54", value: "45-54" },
    { label: "55+", value: "55+" },
  ];

  const handleGenerateMore = useSerializeCallback(async () => {
    report.click({ kalo_type: rewriteScriptList?.length >= 2 ? "regenerate" : "generate" });
    try {
      const res: any = await dispatch(fetchAiCredits());
      const totalRemain = res.payload.totalRemain || 0;
      if (totalRemain < 2) {
        message.error("Not enough credits");
        return;
      }
      setIsOpen(false);
      loopAITask && (await loopAITask());
    } catch (error: any) {
      message.error(error?.message || "Failed to check credits");
    }
  });

  const btnDisabled = useMemo(() => {
    return status !== "idle" || (!videoId && !productItem.data);
  }, [rewriteScriptList, status, videoId, productItem.data]);

  const renderHeader = () => {
    if (status !== "idle") return null;
    return (
      <div className="p-[20px] text-center border-b border-kds-border bg-kds-bg-soft relative">
        <div className={classNames("text-[24px] font-bold", isDark ? "kalo-text-linear-gray" : "text-kds-fg")}>
          <Trans>AI Script Writer</Trans>
        </div>
        <div className="text-[14px] text-kds-fg-soft">
          <Trans>Turn Kalodata Trends into High-Converting Scripts.</Trans>
        </div>
        {status === "idle" && (
          <div
            className={classNames(
              "absolute top-[16px] right-[16px] text-[14px] font-normal text-kds-fg-soft flex items-center gap-[4px] cursor-pointer",
              tryExampleLoading && "pointer-events-none opacity-70",
            )}
            onClick={async () => {
              report.click({ kalo_type: "try_an_example" });
              setTryExampleLoading(true);
              if (tryExampleLoading) return;
              try {
                const data: any = await aiStudioService.fetchExampleVideo();
                if (data?.videoId != null && data?.videoSource != null && data?.country != null) {
                  window.open(
                    `/script-rewrite?id=${data.videoId}&videoSource=${data.videoSource}&country=${data.country}`,
                    "_blank",
                  );
                } else {
                  message.error(t("Failed to load example"));
                }
              } catch {
                message.error(t("Failed to load example"));
              } finally {
                setTryExampleLoading(false);
              }
            }}
          >
            <IconAiGradient />
            <Trans>Try an Example</Trans>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      className={classNames(
        "VideoParamsPanel relative h-full group flex flex-col",
        status === "idle"
          ? "w-[720px]"
          : isOpen
            ? "w-[40%] VideoParamsPanel-generate transition-all duration-300"
            : "w-[332px] VideoParamsPanel-generate transition-all duration-300",
        showCard && "bg-kds-bg border border-kds-border rounded-none! border-t-0",
        className,
      )}
    >
      {renderHeader()}
      <div
        className={classNames(
          "kds-scroll-thin flex-1 overflow-y-auto overflow-x-hidden p-[16px] pb-[80px]",
          status === "idle" && "pt-[24px]",
        )}
      >
        {/* Reference Video */}
        {(status === "idle" || videoId) && (
          <VideoReference
            videoId={videoId}
            videoDetail={videoDetail}
            status={status}
            disabled={btnDisabled}
            isOpen={isOpen}
            handleShowModal={setShowVideoModal}
          />
        )}
        {videoId && (
          <section className="mb-[16px]">
            <div className="flex items-center gap-[4px] font-semibold mb-2 text-[16px]">
              <Trans>Similarity Level</Trans>
            </div>
            <SelectGroup
              disabled={btnDisabled}
              options={levelOptions}
              value={imitation_level}
              onChange={(v) => dispatch(updateParams({ imitation_level: v } as any))}
            />
          </section>
        )}

        {/* Product to Promote */}
        {(status === "idle" || productItem.data) && (
          <SearchProductInput
            popoverWidth={popoverWidth}
            disabled={btnDisabled}
            isOpen={isOpen}
            handleShowModal={setShowProductModal}
          />
        )}

        {/* Selling Points */}
        {productItem.data && (
          <section>
            <div className="flex items-center gap-[4px] font-semibold mb-2 text-[16px] mt-[16px]">
              <Trans>Selling Points</Trans>
            </div>
            <SellingPoints
              externalData={
                btnDisabled
                  ? { ...sellingPointsData, data: (sellingPointsData.data || []).filter((sp: any) => sp.checked) }
                  : sellingPointsData
              }
              isOpen={isOpen}
              popoverWidth={popoverWidth}
              disabled={btnDisabled}
              onChange={(data) => {
                dispatch(
                  updateParams({
                    sellingPoints: { data, status: "succeeded" },
                    selling_points: data.filter((item: any) => item.checked).map((item: any) => item.keyWord),
                  } as any),
                );
              }}
            />
          </section>
        )}

        {/* Advanced Options */}
        <section className="mt-[16px]">
          <div
            className="text-kds-fg-subtle py-[4px] flex items-center justify-between cursor-pointer select-none border-b border-kds-border text-[12px]"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <Trans>Advanced Options</Trans>
            <IconArrowDown
              className={`w-[16px] h-[16px] text-kds-fg transition-transform duration-300 base-filter ${showAdvanced ? "rotate-180" : ""}`}
            />
          </div>
          {showAdvanced && (
            <div className="mt-4 flex flex-col gap-4">
              {/* Gender */}
              <section>
                <div className="flex items-center gap-[4px] font-semibold mb-2 text-[16px]">
                  <Trans>Gender</Trans>
                </div>
                <SelectGroup
                  disabled={btnDisabled}
                  options={genderOptions}
                  value={target_gender}
                  onChange={(e) => dispatch(updateParams({ target_gender: e } as any))}
                />
              </section>
              {/* Age */}
              <section>
                <div className="flex items-center gap-[4px] font-semibold mb-2 text-[16px]">
                  <Trans>Age</Trans>
                </div>
                <SelectGroup
                  disabled={btnDisabled}
                  options={ageOptions}
                  value={target_age}
                  onChange={(e) => dispatch(updateParams({ target_age: e } as any))}
                  className="flex-wrap"
                />
              </section>
              {/* Language */}
              <section>
                <div className="flex items-center gap-[4px] font-semibold mb-2 text-[16px]">
                  <Trans>Language</Trans>
                  <Tooltip
                    title={
                      <Trans values={{ auto: t("Auto") }}>
                        {`Choose the language for the final script. '{{auto}}' will match the language of the reference video.`}
                      </Trans>
                    }
                    getPopupContainer={(node) => node?.parentElement || document.body}
                  >
                    <span>
                      <IconHelp className="w-[16px] h-[16px]" />
                    </span>
                  </Tooltip>
                </div>
                <SelectLanguage
                  prefixAutoOption
                  value={language}
                  onChange={(e) => dispatch(updateParams({ language: e } as any))}
                  disabled={btnDisabled}
                  listHeight={400}
                  className="h-[36px]"
                />
              </section>
            </div>
          )}
        </section>
      </div>

      {/* 按钮区 */}
      <div className="VideoParamsPanel-btns absolute bottom-0 left-0 right-0 z-10 flex justify-between items-center gap-[8px] mt-6 h-[72px] px-[16px] border-t border-kds-border">
        {status === "idle" && (
          <Tooltip
            title={
              rewriteScriptList?.length === 4
                ? t("The generation limit has been reached. Please refresh the page to continue.")
                : null
            }
          >
            <CommonButton
              size="large"
              className={classNames(
                "flex-1 h-[34px] py-2 text-kds-fg-subtle rounded font-semibold border border-kds-border px-0",
                btnDisabled && "bg-disable-btn! text-kds-fg border-kds-border",
              )}
              disabled={rewriteScriptList?.length === 4}
              onClick={() => {
                if (originalVideoID) {
                  reset();
                  dispatch(
                    updateParams({
                      target_age: "all",
                      target_gender: "all",
                      imitation_level: "standard",
                      language: "auto",
                    } as any),
                  );
                } else {
                  dispatch(
                    updateParams({
                      videoId: "",
                      videoSource: "",
                      hasProduct: false,
                      videoDetail: { status: "idle", data: null },
                      productItem: { status: "idle", data: null },
                      productList: { status: "idle", data: [] },
                      sellingPoints: { status: "idle", data: [] },
                      selling_points: [],
                      target_age: "all",
                      target_gender: "all",
                      imitation_level: "standard",
                      language: "auto",
                    } as any),
                  );
                }
              }}
            >
              <Trans>Reset</Trans>
            </CommonButton>
          </Tooltip>
        )}
        {renderGenerateBtn()}
      </div>

      {/* 展开开关 */}
      {/* {status !== "idle" && (
        <div
          className="absolute right-[16px] -top-[36px] z-20 cursor-pointer w-[20px] h-[20px] flex-center"
          onClick={() => setIsOpen((v) => !v)}
        >
          <IconArrow className={classNames("w-[20px] h-[20px]", !isOpen && "rotate-180")} />
        </div>
      )} */}

      {showVideoModal && <VideoLibraryModal open={showVideoModal} onClose={() => setShowVideoModal(false)} />}
      {showProductModal && (
        <ProductLibraryModal
          open={showProductModal}
          onClose={() => setShowProductModal(false)}
          onSubmit={(product: any) => {
            dispatch(updateProductItem(product));
            requestSellingPointers(product.id, product.country);
          }}
        />
      )}
    </div>
  );
};

export default VideoParamsPanel;
